import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { provideComponentStore } from '@ngrx/component-store';
import { AddSmartSearchNewPropertyInvestmentTypeSelectionComponent } from '@platform/smart-search/components/add-smart-search-new-property-investment-type-selection/add-smart-search-new-property-investment-type-selection.component';
import { AddSmartSearchNewPropertyMaximumPriceComponent } from '@platform/smart-search/components/add-smart-search-new-property-maximum-price/add-smart-search-new-property-maximum-price.component';
import { AddSmartSearchNewPropertyPurchaseTypeSelectionComponent } from '@platform/smart-search/components/add-smart-search-new-property-purchase-type-selection/add-smart-search-new-property-purchase-type-selection.component';
import { AddSmartSearchPropertySelectionComponent } from '@platform/smart-search/components/add-smart-search-property-selection/add-smart-search-property-selection.component';
import { AddSmartSearchRemortgageBorrowMoreComponent } from '@platform/smart-search/components/add-smart-search-remortgage-borrow-more/add-smart-search-remortgage-borrow-more.component';
import { AddSmartSearchRemortgagePayMortgageFasterComponent } from '@platform/smart-search/components/add-smart-search-remortgage-pay-mortgage-faster-payment/add-smart-search-remortgage-pay-mortgage-faster.component';
import { AddSmartSearchRemortgageSpeakToAnExpertComponent } from '@platform/smart-search/components/add-smart-search-remortgage-speak-to-an-expert/add-smart-search-remortgage-speak-to-an-expert.component';
import { AddSmartSearchSpecifySearchComponent } from '@platform/smart-search/components/add-smart-search-specify-search/add-smart-search-specify-search.component';
import { AddSmartSearchTypeSelectionComponent } from '@platform/smart-search/components/add-smart-search-type-selection/add-smart-search-type-selection.component';
import { AddSmartSearchDialogStep } from '@platform/smart-search/enums/add-smart-search-dialog-step.enum';
import { SmartSearchType } from '@platform/smart-search/enums/smart-search-type.enum';
import { smartSearchTypeHeadlineMap } from '@platform/smart-search/maps/smartSearchTypeHeadlineMap';
import { AddSmartSearchDialogStore } from '@platform/smart-search/pages/add-smart-search-dialog/add-smart-search-dialog.store';
import { AddSmartSearchDialogFormRawValues, buildAddSmartSearchDialog } from '@platform/smart-search/utils/formUtils';
import { ModalContainerComponent } from '@shared-lib/components/modal-container/modal-container.component';
import { ScreenSizeBreakpointNames } from '@shared-lib/services/screen-size-breakpoint-observer.service';

@Component({
  selector: 'cp-add-smart-search-dialog',
  imports: [
    CommonModule,
    ModalContainerComponent,
    AddSmartSearchTypeSelectionComponent,
    AddSmartSearchPropertySelectionComponent,
    ReactiveFormsModule,
    AddSmartSearchRemortgageBorrowMoreComponent,
    AddSmartSearchSpecifySearchComponent,
    AddSmartSearchNewPropertyInvestmentTypeSelectionComponent,
    AddSmartSearchRemortgagePayMortgageFasterComponent,
    AddSmartSearchRemortgageSpeakToAnExpertComponent,
    AddSmartSearchNewPropertyMaximumPriceComponent,
    AddSmartSearchNewPropertyPurchaseTypeSelectionComponent,
  ],
  templateUrl: './add-smart-search-dialog.component.html',
  styleUrls: ['./add-smart-search-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideComponentStore(AddSmartSearchDialogStore)],
  animations: [
    trigger('slideOnEnterAndLeave', [
      transition(':enter', [
        style({
          transform: 'translateX({{ enterTranslate }})',
          position: 'absolute',
          width: '100%',
        }),
        animate('0.25s', style({ transform: 'translateX(0)' })),
      ]),
      transition(':leave', [
        style({ transform: 'translateX(0)', width: '100%' }),
        animate(
          '0.25s',
          style({
            transform: 'translateX({{ leaveTranslate }})',
          }),
        ),
      ]),
    ]),
  ],
})
export class AddSmartSearchDialogComponent implements AfterViewInit {
  private readonly componentStore = inject(AddSmartSearchDialogStore);
  private readonly destroyRef = inject(DestroyRef);

  readonly dialogStep$ = this.componentStore.dialogStep$;
  readonly startingDialogStep$ = this.componentStore.startingDialogStep$;
  readonly isDialogPositionUp$ = this.componentStore.isDialogPositionUp$;
  readonly screenSizeBreakpointName$ = this.componentStore.screenSizeBreakpointName$;
  readonly propertyWithMortgageSelectItems$ = this.componentStore.propertyWithMortgageSelectItems$;
  readonly isSkipPropertySelectPossible$ = this.componentStore.isSkipPropertySelectPossible$;
  readonly isLoadingPlatformItems$ = this.componentStore.isLoadingPlatformItems$;
  readonly clientUserData$ = this.componentStore.clientUserData$;
  readonly payMortgageFasterSaving$ = this.componentStore.payMortgageFasterSaving$;
  readonly isLoadingPayMortgageFasterSaving$ = this.componentStore.isLoadingPayMortgageFasterSaving$;
  readonly isSaveInProgress$ = this.componentStore.isSaveInProgress$;
  readonly hasClientResidentialProperty$ = this.componentStore.hasClientResidentialProperty$;

  addSmartSearchForm = buildAddSmartSearchDialog();
  isDirectionForward: boolean;

  protected readonly AddSmartSearchDialogStep = AddSmartSearchDialogStep;
  protected readonly ScreenSizeBreakpointNames = ScreenSizeBreakpointNames;
  protected readonly SmartSearchType = SmartSearchType;
  protected readonly smartSearchTypeHeadlineMap = smartSearchTypeHeadlineMap;

  get smartSearchType(): SmartSearchType {
    return this.addSmartSearchForm.value.typeSelection.type;
  }

  ngAfterViewInit(): void {
    this.componentStore.moveDialogUp();

    this.addSmartSearchForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((formValue: AddSmartSearchDialogFormRawValues) => {
        this.componentStore.setFormValue(formValue);
      });

    this.componentStore.formValue$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((formValue) => {
      this.addSmartSearchForm.patchValue(formValue, { emitEvent: false, onlySelf: true });
    });
  }

  nextStep(): void {
    this.isDirectionForward = true;
    setTimeout(() => this.componentStore.nextStep(), 10);
  }

  previousStep(): void {
    this.isDirectionForward = false;
    setTimeout(() => this.componentStore.previousStep(), 10);
  }

  closeForm(): void {
    this.componentStore.closeDialog(null);
  }

  calculatePayMortgageFasterSaving(calculateSavingData: { termInMonths?: number; monthlyPaymentIncrease?: number }): void {
    this.componentStore.calculatePayMortgageFasterSaving(calculateSavingData);
  }
}
